import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { tracked } from '@glimmer/tracking';
import { ALL_CHANNELS, Channel, Periods } from 'cio/utils/metrics/common';
import { Grouping } from 'cio/utils/metrics/resolution';
import { LEADERBOARD_TABS } from 'cio/utils/campaign-overview/leaderboard';
import { SortDirection } from 'cio/utils/sort';
import { action } from '@ember/object';
import { ActionType } from 'cio/models/action';
import { actionTypeName } from 'cio/utils/action-info';
import AnalyticsService from 'cio/services/analytics';
import moment from 'moment-timezone';
import { EditType } from 'cio/utils/analytics/transforms/campaign';
import { LAST_30_DAYS_RANGE_PRESET } from '@customerio/fly-components/utils/fly-date-picker-utils';
import CampaignModel, { RESTRICTED_ACTION_TYPES } from 'cio/models/campaign';
import { isEmpty } from '@ember/utils';
import SettingsService from 'cio/services/settings';
import { schedule } from '@ember/runloop';

const CHANNELS_QUERY_PARAM_SEPARATOR = '-';

export default class OverviewIndexController extends Controller {
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service settings!: SettingsService;

  declare model: CampaignModel;

  queryParams = [
    {
      startTime: { as: 'start_time' },
      endTime: { as: 'end_time' },
      period: { as: 'period' },
      grouping: { as: 'grouping' },
      channelsParam: { as: 'channels' },
      leaderboardTab: { as: 'leaderboard_tab' },
      leaderboardSortDirection: { as: 'leaderboard_sort_direction' },
    },
  ];

  @tracked startTime = '';
  @tracked endTime = '';

  get timeRange() {
    if (this.startTime === '' || this.endTime === '') {
      return LAST_30_DAYS_RANGE_PRESET.getRange();
    }

    return { start: moment(this.startTime), end: moment(this.endTime) };
  }

  @tracked period = Periods.DAYS;

  @tracked grouping = Grouping.Daily;

  @tracked channels: Channel[] = [];
  @tracked channelsParam: string = this.channels.join(CHANNELS_QUERY_PARAM_SEPARATOR);

  @tracked leaderboardTab: string = LEADERBOARD_TABS[0].value;

  @tracked leaderboardSortDirection: SortDirection = SortDirection.Descending;

  @action
  deserializeQueryParams() {
    let channels = this.channelsParam === '' ? [] : this.channelsParam.split(CHANNELS_QUERY_PARAM_SEPARATOR);

    this.channels = this.environment.availableMeasurableActionTypes.filter((v) => channels.includes(v)) as Channel[];
  }

  @action
  serializeQueryParams() {
    this.channelsParam = this.channels.join(CHANNELS_QUERY_PARAM_SEPARATOR);
  }

  @action
  onTimeRangeChange(startTime: string, endTime: string) {
    this.startTime = startTime;
    this.endTime = endTime;

    this.analytics.track('campaign', 'overview_timeframe_filter_changed', {
      model: this.model,
      timePeriod: this.period,
      timeRange: this.timeRange,
      grouping: this.grouping,
      channels: this.channels,
      editType: EditType.TimeRange,
    });
  }

  @action
  onPeriodChange(period: Periods) {
    this.period = period;
  }

  @action
  onGroupingChange(grouping: Grouping, trackEvent = true) {
    this.grouping = grouping;

    if (trackEvent) {
      this.analytics.track('campaign', 'overview_timeframe_filter_changed', {
        model: this.model,
        timePeriod: this.period,
        timeRange: this.timeRange,
        grouping: this.grouping,
        channels: this.channels,
        editType: EditType.Grouping,
      });
    }
  }

  @action
  onChannelsChange(channels: Channel[]) {
    // We set channels like this rather than directly to maintain the default
    // sort order
    this.channels = [...channels].sort((a, b) => ALL_CHANNELS.indexOf(a) - ALL_CHANNELS.indexOf(b));

    this.analytics.track('campaign', 'overview_timeframe_filter_changed', {
      model: this.model,
      timePeriod: this.period,
      timeRange: this.timeRange,
      grouping: this.grouping,
      channels: this.channels,
      editType: EditType.Channels,
    });

    this.serializeQueryParams();
  }

  @action
  onLeaderboardTabChange(tab: string) {
    this.leaderboardTab = tab;
  }

  @action
  onLeaderboardSortChange(sortDirection: SortDirection) {
    this.leaderboardSortDirection = sortDirection;
  }

  @action
  resetQueryParams() {
    this.startTime = '';
    this.endTime = '';
    this.period = Periods.DAYS;
    this.grouping = Grouping.Daily;
    this.channels = this.getAllAvailableChannels();
    this.leaderboardTab = LEADERBOARD_TABS[0].value;
    this.leaderboardSortDirection = SortDirection.Descending;

    this.serializeQueryParams();
  }

  @action
  getAllAvailableChannels() {
    return [...ALL_CHANNELS]
      .map((channel) => `${channel}_action`)
      .filter((type: ActionType) => {
        let restrictedTypes = RESTRICTED_ACTION_TYPES[this.model.type] ?? [];
        return !restrictedTypes.includes(type) && this.environment.enabledAction(type);
      })
      .map((type: ActionType) => actionTypeName(type)) as Channel[];
  }

  @action
  setAllAvailableChannels() {
    if (this.channels.length === 0) {
      let availableChannels = this.getAllAvailableChannels();
      this.channels = [...availableChannels];
      this.channelsParam = availableChannels.join(CHANNELS_QUERY_PARAM_SEPARATOR);
    }
  }

  // If the endTime query param is a future date, reset it to today (we can't
  // retrieve metrics from the future).
  @action
  limitEndTime() {
    if (isEmpty(this.endTime)) {
      return;
    }

    let endMoment = moment.tz(this.endTime, this.settings.environmentSettings.metricsTimezone).startOf('day');
    let maxMoment = moment.tz(this.settings.environmentSettings.metricsTimezone).startOf('day');

    if (endMoment.isAfter(maxMoment)) {
      // Run this in the routerTransitions hook to make sure the query param in
      // the URL gets updated.
      schedule('routerTransitions', () => {
        this.endTime = maxMoment.format('YYYY-MM-DD');
      });
    }
  }
}
